import { getConfig, useModuleFederationContext, useTranslation } from '@msdk/common/'
import { EmptyStateMessageDefinition } from '@msdk/definitions/src/definitions/view-definitions/empty-state-message-definition'
import { EmptyState, styled, useComponentLibrary } from '@msdk/ui'
import React from 'react'

import { resolveItem } from '../../../utils/resolve-item'
import { stringFormat } from '../../../utils/string-format'
import { useDynamicViewContext } from '../context'
import { EmptyStateOverlayVisualization } from './EmptyStateOverlayVisualization'
import NoResultIcon from './NoResultIcon'

const OverlayWrapper = styled('div')`
  flex: 1;
  inset: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;
  gap: 10px;
  min-height: 240px;
`

interface Props {
  visualization?: EmptyStateOverlayVisualization
}

export function EmptyStateOverlay({ visualization = EmptyStateOverlayVisualization.TextAndImage }: Props) {
  const { viewModel, currentFilters, additionalCondition, entityModel, baseMetadata } = useDynamicViewContext()

  const t = useTranslation()
  const library = useComponentLibrary()
  const entityDescription = t(entityModel.idml, entityModel.description)

  const modules = useModuleFederationContext()
  const msModule = modules.loadedModules[baseMetadata.msProvider]
  const ImportedComponent = resolveItem(msModule, viewModel.emptyStateBehaviour?.message?.customUI)
  const ImportedComponentFiltersApplied = resolveItem(
    msModule,
    viewModel.emptyStateBehaviour?.messageFiltersApplied?.customUI,
  )

  const hasFilters = Object.keys(currentFilters).some(
    (x) => currentFilters[x] && viewModel.filters.find((y) => y.parameterKey == x)?.visible,
  )
  const hasAdditionalContitions = additionalCondition.conditions.length > 0
  const hasFiltersOrAdditionalConditions = hasFilters || hasAdditionalContitions

  const getTranslatedDesc = (message?: EmptyStateMessageDefinition) => {
    let retVal = ''
    if (message?.idml && message?.description) {
      retVal = t(message.idml, message.description)
    }
    return retVal
  }
  const translatedDesc = getTranslatedDesc(viewModel.emptyStateBehaviour?.message)
  const translatedDescFiltersApplied = getTranslatedDesc(viewModel.emptyStateBehaviour?.messageFiltersApplied)

  const urlRoot = getConfig().API
  const sanitizedUrl = `${baseMetadata.msProvider}/${viewModel.emptyStateBehaviour?.image?.path}`.replace(/\/\//g, '/')
  const pathImage = `${urlRoot}/${sanitizedUrl}`

  const showImage = visualization === EmptyStateOverlayVisualization.TextAndImage && !hasFiltersOrAdditionalConditions

  const useDefaultMessage =
    !ImportedComponent &&
    !ImportedComponentFiltersApplied &&
    !viewModel.emptyStateBehaviour?.message &&
    !viewModel.emptyStateBehaviour?.messageFiltersApplied

  return (
    <>
      <OverlayWrapper>
        {showImage && viewModel.emptyStateBehaviour?.image?.path && !hasFiltersOrAdditionalConditions && (
          <EmptyState title="" illustration={pathImage} />
        )}
        {showImage && !viewModel.emptyStateBehaviour?.image?.path && library !== 'nova' && (
          <EmptyState title="" illustration={<NoResultIcon />} />
        )}
        {!hasFiltersOrAdditionalConditions && ImportedComponent && <ImportedComponent />}
        {!hasFiltersOrAdditionalConditions && !ImportedComponent && translatedDesc != '' && (
          <EmptyState title="" body={translatedDesc} />
        )}
        {hasFiltersOrAdditionalConditions && ImportedComponentFiltersApplied && <ImportedComponentFiltersApplied />}
        {hasFiltersOrAdditionalConditions && !ImportedComponentFiltersApplied && translatedDescFiltersApplied != '' && (
          <EmptyState title="" body={translatedDescFiltersApplied} />
        )}
        {hasFiltersOrAdditionalConditions &&
          !ImportedComponentFiltersApplied &&
          translatedDescFiltersApplied == '' &&
          ImportedComponent && <ImportedComponent />}
        {hasFiltersOrAdditionalConditions &&
          !ImportedComponentFiltersApplied &&
          translatedDescFiltersApplied == '' &&
          !ImportedComponent &&
          translatedDesc != '' && <EmptyState title="" body={translatedDesc} />}
        {useDefaultMessage && hasFiltersOrAdditionalConditions && (
          <EmptyState
            title={stringFormat(t('LBL_EMPTY_STATE_NO_RESULT', 'No results are found.'))}
            body={stringFormat(
              t('LBL_EMPTY_STATE_ADJUST_FILTERS', 'Adjust filters or add new {0}.'),
              entityDescription,
            )}
          />
        )}
        {useDefaultMessage && !hasFiltersOrAdditionalConditions && (
          <EmptyState
            title={stringFormat(t('LBL_EMPTY_STATE_EMPTY_LIST', 'Your {0} list is empty.'), entityDescription)}
            body={stringFormat(t('LBL_EMPTY_STATE_ADD_NEW', 'Add new {0} to fill it up.'), entityDescription)}
          />
        )}
      </OverlayWrapper>
    </>
  )
}
